import { useCallback, useContext } from "react";
import { matchPath, UNSAFE_LocationContext, UNSAFE_NavigationContext } from "react-router-dom";
import { QueryClient, QueryClientContext, useQuery } from "@tanstack/react-query";
import { listStories } from "../../../services/dataStories/storiesApi";
import { fetchStoryFields } from "../../../services/storyCanvas/storyFieldsApi";
import type { StoryFieldApi } from "../../../services/storyCanvas/storyFilters.types";
import { queryKeys } from "../../../services/queryKeys";
import { tryGetActiveWorkspaceId } from "../../../services/workspace/activeWorkspace";
import { WORKSPACE_PARAM } from "../../../hooks/useSelectedWorkspace";

/** Most stories the palette loads to search in the browser (Q2: load all pages, cap 500). */
export const PALETTE_STORY_CAP = 500;
const PAGE_SIZE = 200;
const STALE_MS = 60_000;
const STORY_ROUTE = "/workspace/:workspaceId/story/:storyId/*";

export interface PaletteStory {
  id: string;
  workspaceId: string;
  title: string;
  /** ISO time of the story's last change, for the palette row's "updated …" hint. */
  updatedAt?: string;
}

export interface PaletteSearchSources {
  stories: readonly PaletteStory[];
  /** The current story's real fields; empty outside a story. */
  fields: readonly StoryFieldApi[];
  /** The story the page is on, or null. */
  storyId: string | null;
  openStory: (story: PaletteStory) => void;
}

/** Used when the palette is mounted without a QueryClientProvider (tests, stories). */
const FALLBACK_CLIENT = new QueryClient({ defaultOptions: { queries: { retry: false } } });

/** Pages the stories list until the cap or the end. A failure yields what was loaded, never an error row. */
export const loadStoriesForPalette = async (workspaceId: string): Promise<PaletteStory[]> => {
  const byId = new Map<string, PaletteStory>();
  for (let offset = 0; offset < PALETTE_STORY_CAP; offset += PAGE_SIZE) {
    const page = await listStories(workspaceId, { limit: Math.min(PAGE_SIZE, PALETTE_STORY_CAP - offset), offset });
    page.items.forEach((item) => {
      if (!byId.has(item.id)) byId.set(item.id, { id: item.id, workspaceId: item.workspace_id, title: item.title?.trim() || "Untitled story", updatedAt: item.updated_at });
    });
    if (page.items.length === 0 || offset + page.items.length >= page.total) break;
  }
  return Array.from(byId.values());
};

/**
 * What the palette searches besides commands: the workspace's stories (loaded once while the palette is
 * open) and, inside a story, that story's fields. Works without a router or query client (returns nothing).
 * PENDING-SEARCH-STORIES / PENDING-SEARCH-FIELDS: the integrator may dedupe the stories load with the
 * Data Stories page's load-all hook.
 */
export const usePaletteSearchSources = (open: boolean): PaletteSearchSources => {
  const location = useContext(UNSAFE_LocationContext)?.location ?? null;
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator ?? null;
  const client = useContext(QueryClientContext) ?? FALLBACK_CLIENT;
  const route = location ? matchPath(STORY_ROUTE, location.pathname) : null;
  const storyId = route?.params.storyId ?? null;
  const workspaceId = route?.params.workspaceId ?? (location ? new URLSearchParams(location.search).get(WORKSPACE_PARAM) : null) ?? (location ? tryGetActiveWorkspaceId() : null);

  const storiesQuery = useQuery(
    {
      queryKey: [...queryKeys.stories.list(workspaceId ?? ""), "palette", PALETTE_STORY_CAP],
      queryFn: () => loadStoriesForPalette(workspaceId as string),
      enabled: open && Boolean(workspaceId),
      staleTime: STALE_MS,
      retry: false,
    },
    client,
  );
  const fieldsQuery = useQuery(
    {
      queryKey: queryKeys.dataModels.storyFields(storyId ?? "", "palette"),
      queryFn: () => fetchStoryFields(storyId as string, {}),
      enabled: open && Boolean(storyId),
      staleTime: STALE_MS,
      retry: false,
    },
    client,
  );

  const openStory = useCallback(
    (story: PaletteStory) => navigator?.push(`/workspace/${story.workspaceId}/story/${story.id}/build`),
    [navigator],
  );

  return { stories: storiesQuery.data ?? [], fields: storyId ? (fieldsQuery.data?.fields ?? []) : [], storyId, openStory };
};
